/**
 * The upload modal's per-file state, as pure functions.
 *
 * Extracted from the component because the bug this replaces was a state bug, not a rendering
 * one: the old modal drove one shared progress bar from the S3 upload, then set it back to 0 when
 * the upload finished so the parse could reuse it. The bar visibly counted down, and then sat at
 * 0 for the whole parse — because the parse's progress branch listened for a socket status string
 * ("In Process") the backend never sent, divided by a `processed_count` the backend never emitted.
 *
 * The rule that prevents all of that, and the one thing worth testing here: a file's progress is
 * monotonic. It never decreases. When the determinate phase (bytes to S3) ends, the row does not
 * reset — it switches to an indeterminate phase, which makes no claim about percentage at all.
 */

import { RESUME_STATUS, isTerminal } from "./resumeStatus";

export const PHASE = {
  UPLOADING: "uploading",   // determinate: we know the byte count
  PROCESSING: "processing", // indeterminate: the server knows, and it isn't measured in bytes
  DONE: "done",
  FAILED: "failed",
};

export const initialFiles = (files) =>
  files.map((f) => ({ name: f.name, phase: PHASE.UPLOADING, percent: 0, error: null }));

/**
 * Advance the determinate phase. Clamps to [0, 100] and never moves backwards.
 *
 * The clamp is not defensive padding — axios fires onUploadProgress per chunk across the whole
 * multipart body, and a retried chunk can report a lower cumulative figure than one already seen.
 */
export const withUploadPercent = (files, percent) => {
  const next = Math.max(0, Math.min(100, Math.round(percent)));
  return files.map((f) =>
    f.phase === PHASE.UPLOADING ? { ...f, percent: Math.max(f.percent, next) } : f
  );
};

/** The bytes are in S3. Hand off to the server; stop pretending to know a percentage. */
export const toProcessing = (files) =>
  files.map((f) =>
    f.phase === PHASE.UPLOADING ? { ...f, phase: PHASE.PROCESSING, percent: 100 } : f
  );

export const allFailed = (files, error) =>
  files.map((f) => ({ ...f, phase: PHASE.FAILED, error }));

/**
 * Fold the server's per-file statuses back into the rows.
 *
 * Matched by filename because that is the only handle the client has — it never sees the s3_key.
 * Duplicate filenames within one upload therefore share a row's fate, which is acceptable: the
 * authoritative per-file view is the candidates table, which keys on resume id.
 */
export const withServerStatuses = (files, statuses = []) => {
  const byName = new Map();
  statuses.forEach((s) => {
    if (s.filename) byName.set(s.filename, s);
  });

  return files.map((f) => {
    // A file still streaming bytes has not reached the server yet; anything the server says about
    // an earlier upload of the same name is not about this one.
    if (f.phase === PHASE.UPLOADING) return f;

    const s = byName.get(f.name);
    if (!s) return f;

    if (s.status === RESUME_STATUS.FAILED || s.is_stale) {
      return {
        ...f,
        phase: PHASE.FAILED,
        error: s.error || "Processing stopped unexpectedly.",
      };
    }
    if (s.status === RESUME_STATUS.SCORED) return { ...f, phase: PHASE.DONE };
    return { ...f, phase: PHASE.PROCESSING, serverStatus: s.status };
  });
};

/** Keep polling only while some row can still change on its own. */
export const isSettled = (files) =>
  files.every((f) => f.phase === PHASE.DONE || f.phase === PHASE.FAILED);

/** Everything landed and scored — the only state in which "View rankings" means anything. */
export const isFullyScored = (files) =>
  files.length > 0 && files.every((f) => f.phase === PHASE.DONE);

export const statusLabel = (f) => {
  switch (f.phase) {
    case PHASE.UPLOADING:
      return `Uploading ${f.percent}%`;
    case PHASE.PROCESSING:
      if (f.serverStatus === RESUME_STATUS.SCORING) return "Scoring…";
      if (f.serverStatus === RESUME_STATUS.PARSED) return "Waiting to score…";
      if (f.serverStatus === RESUME_STATUS.PARSING) return "Parsing…";
      return "Processing…";
    case PHASE.DONE:
      return "Scored ✓";
    case PHASE.FAILED:
      return "Failed";
    default:
      return "";
  }
};

export { isTerminal };
